import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  where
} from 'firebase/firestore'
import { db } from '../firebase'
import { CODEX_ENTRIES } from '../data/codexEntries'

const CODEX_DOCUMENTS = 'codexDocuments'
const CODEX_DRAFTS = 'codexDrafts'
const CODEX_REVISIONS = 'codexRevisions'
const CODEX_AUDIT_LOGS = 'codexAuditLogs'

export const CODEX_TEMPLATES = [
  { id: 'theorie', label: 'Fiche théorie', blocks: [{ type: 'heading', text: 'Introduction' }, { type: 'paragraph_markdown', text: 'Contenu principal à compléter.' }] },
  { id: 'style', label: 'Fiche style', blocks: [{ type: 'heading', text: 'Repères stylistiques' }, { type: 'paragraph_markdown', text: '- Contexte\n- Caractéristiques\n- Exemples' }] },
  { id: 'compositeur', label: 'Fiche compositeur', blocks: [{ type: 'heading', text: 'Biographie courte' }, { type: 'paragraph_markdown', text: 'Dates, contexte, esthétique.' }] },
  { id: 'forme', label: 'Fiche forme', blocks: [{ type: 'heading', text: 'Définition' }, { type: 'paragraph_markdown', text: 'Structure, indices d’écoute, exemples.' }] }
]

function nowIso() {
  return new Date().toISOString()
}

function normalizeSlug(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80)
}

function sanitizeText(value) {
  const str = String(value || '')
  return str.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '').trim()
}

export function sanitizeCodexBlocks(blocks) {
  if (!Array.isArray(blocks)) return []
  const allowedTypes = new Set(['heading', 'paragraph_markdown', 'callout', 'example_music', 'image', 'divider'])
  return blocks
    .filter((b) => b && allowedTypes.has(b.type))
    .map((b) => {
      const block = {
        type: b.type,
        text: sanitizeText(b.text || ''),
        title: sanitizeText(b.title || '')
      }
      if (b.type === 'image') {
        block.url = sanitizeText(b.url || '')
        block.caption = sanitizeText(b.caption || '')
      }
      return block
    })
}

export function validateDraftForPublish(draft) {
  const errors = []
  if (!draft?.title || !String(draft.title).trim()) errors.push('Titre requis')
  if (!draft?.shortDescription || !String(draft.shortDescription).trim()) errors.push('Description courte requise')
  if (!Array.isArray(draft?.contentBlocks) || draft.contentBlocks.length === 0) errors.push('Au moins un bloc de contenu requis')
  return errors
}

/** Texte d’un bloc pour le corps d’une section (les titres viennent des blocs heading). */
function blockBodyTextForSection(b) {
  if (!b || b.type === 'divider' || b.type === 'heading') return ''
  switch (b.type) {
    case 'paragraph_markdown':
    case 'callout':
    case 'example_music':
      return String(b.text || '').trim()
    case 'image':
      if (b.caption) return String(b.caption).trim()
      if (b.url) return String(b.url).trim()
      return ''
    default:
      return String(b.text || '').trim()
  }
}

/**
 * Regroupe les contentBlocks publiés en sections : une entrée TOC par titre (heading),
 * le texte suivant jusqu’au prochain heading dans le corps. Évite d’afficher les types bruts (paragraph_markdown, etc.).
 */
export function buildPublishedEntrySections(blocks) {
  if (!Array.isArray(blocks) || blocks.length === 0) return []

  const sections = []
  let sectionTitle = null
  let bodyParts = []

  const introTitleFromParts = (parts) => {
    const flat = parts.join(' ').replace(/\s+/g, ' ').trim()
    if (!flat) return 'Introduction'
    if (flat.length <= 72) return flat
    return `${flat.slice(0, 69)}…`
  }

  const flush = (title, parts) => {
    const content = parts.filter(Boolean).join('\n\n').trim()
    const tit = title == null ? introTitleFromParts(parts) : String(title).trim() || 'Sans titre'
    if (!content && title == null) return
    sections.push({
      id: `section-${sections.length + 1}`,
      title: tit,
      content
    })
  }

  for (const b of blocks) {
    if (b.type === 'divider') continue

    if (b.type === 'heading') {
      const nextTitle = String(b.text || '').trim() || 'Sans titre'
      if (sectionTitle !== null) {
        flush(sectionTitle, bodyParts)
        bodyParts = []
      } else if (bodyParts.length) {
        flush(null, bodyParts)
        bodyParts = []
      }
      sectionTitle = nextTitle
    } else {
      const t = blockBodyTextForSection(b)
      if (t) bodyParts.push(t)
    }
  }

  if (sectionTitle !== null) {
    flush(sectionTitle, bodyParts)
  } else if (bodyParts.length) {
    flush(null, bodyParts)
  }

  return sections.map((s, i) => ({ ...s, id: `section-${i + 1}` }))
}

function mapPublishedToEntry(document) {
  const p = document.published || {}
  const blocks = p.contentBlocks || []
  return {
    id: document.id,
    title: p.title || 'Sans titre',
    shortDescription: p.shortDescription || '',
    content: blocks
      .filter((b) => b.type === 'paragraph_markdown' || b.type === 'heading')
      .map((b) => (b.type === 'heading' ? `**${b.text}**` : b.text))
      .join('\n\n'),
    sections: buildPublishedEntrySections(blocks),
    relatedNodeIds: p.relatedNodeIds || [],
    relatedTags: p.relatedTags || [],
    category: p.category || 'parcours',
    order: Number.isFinite(p.order) ? p.order : 999
  }
}

function entryToBlocks(entry) {
  if (Array.isArray(entry.sections) && entry.sections.length > 0) {
    const blocks = []
    entry.sections.forEach((section, idx) => {
      blocks.push({ type: 'heading', text: sanitizeText(section.title || `Section ${idx + 1}`) })
      blocks.push({ type: 'paragraph_markdown', text: sanitizeText(section.content || '') })
    })
    return blocks
  }

  const raw = String(entry.content || '').split('\n\n').map((p) => sanitizeText(p)).filter(Boolean)
  if (raw.length === 0) return [{ type: 'paragraph_markdown', text: '' }]
  return raw.map((p) => ({ type: 'paragraph_markdown', text: p }))
}

export async function listCodexAdminDocs(options = {}) {
  const { includeArchived = false } = options
  const docsQ = query(collection(db, CODEX_DOCUMENTS), orderBy('updatedAt', 'desc'), limit(200))
  const snap = await getDocs(docsQ)
  const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
  return includeArchived ? docs : docs.filter((d) => d.status !== 'archived')
}

export async function getCodexDoc(id) {
  const [docSnap, draftSnap] = await Promise.all([
    getDoc(doc(db, CODEX_DOCUMENTS, id)),
    getDoc(doc(db, CODEX_DRAFTS, id))
  ])
  if (!docSnap.exists()) return null
  return {
    id: docSnap.id,
    ...docSnap.data(),
    draft: draftSnap.exists() ? draftSnap.data() : null
  }
}

export async function createCodexDraft(payload, actor) {
  const slug = normalizeSlug(payload?.slug || payload?.title || `fiche-${nowIso()}`)
  const baseDraft = {
    title: sanitizeText(payload?.title || 'Nouvelle fiche'),
    shortDescription: sanitizeText(payload?.shortDescription || ''),
    category: payload?.category || 'parcours',
    order: Number.isFinite(payload?.order) ? payload.order : 999,
    relatedNodeIds: Array.isArray(payload?.relatedNodeIds) ? payload.relatedNodeIds : [],
    relatedTags: Array.isArray(payload?.relatedTags) ? payload.relatedTags : [],
    contentBlocks: sanitizeCodexBlocks(payload?.contentBlocks || [{ type: 'paragraph_markdown', text: '' }]),
    etag: `${Date.now()}`
  }
  const created = await addDoc(collection(db, CODEX_DOCUMENTS), {
    slug,
    status: 'draft',
    version: 0,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: actor?.uid || null,
    updatedBy: actor?.uid || null,
    published: null
  })
  await setDoc(doc(db, CODEX_DRAFTS, created.id), {
    ...baseDraft,
    updatedAt: serverTimestamp(),
    updatedBy: actor?.uid || null
  })
  await addCodexAuditLog(created.id, 'createDraft', actor)
  return created.id
}

export async function saveCodexDraft(id, payload, actor) {
  const draftRef = doc(db, CODEX_DRAFTS, id)
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(draftRef)
    if (!snap.exists()) throw new Error('Brouillon introuvable')
    const previous = snap.data()
    if (payload?.expectedEtag && previous.etag && payload.expectedEtag !== previous.etag) {
      throw new Error('Conflit de sauvegarde: brouillon modifié ailleurs')
    }
    tx.update(draftRef, {
      title: sanitizeText(payload?.title),
      shortDescription: sanitizeText(payload?.shortDescription),
      category: payload?.category || 'parcours',
      order: Number.isFinite(payload?.order) ? payload.order : 999,
      relatedNodeIds: Array.isArray(payload?.relatedNodeIds) ? payload.relatedNodeIds : [],
      relatedTags: Array.isArray(payload?.relatedTags) ? payload.relatedTags : [],
      contentBlocks: sanitizeCodexBlocks(payload?.contentBlocks || []),
      etag: `${Date.now()}`,
      updatedAt: serverTimestamp(),
      updatedBy: actor?.uid || null
    })
    tx.update(doc(db, CODEX_DOCUMENTS, id), {
      updatedAt: serverTimestamp(),
      updatedBy: actor?.uid || null
    })
  })
  await addCodexAuditLog(id, 'saveDraft', actor)
}

export async function publishCodexDoc(id, actor, options = {}) {
  const publicPreview = options.publicPreview === true
  const documentRef = doc(db, CODEX_DOCUMENTS, id)
  const draftRef = doc(db, CODEX_DRAFTS, id)
  await runTransaction(db, async (tx) => {
    const [docSnap, draftSnap] = await Promise.all([tx.get(documentRef), tx.get(draftRef)])
    if (!docSnap.exists() || !draftSnap.exists()) throw new Error('Document Codex introuvable')
    const current = docSnap.data()
    const draft = draftSnap.data()
    const errors = validateDraftForPublish(draft)
    if (errors.length > 0) throw new Error(errors.join(' | '))
    const nextVersion = (current.version || 0) + 1
    tx.update(documentRef, {
      status: 'published',
      version: nextVersion,
      updatedAt: serverTimestamp(),
      updatedBy: actor?.uid || null,
      publishedAt: serverTimestamp(),
      publishedBy: actor?.uid || null,
      publicPreview,
      published: {
        title: draft.title,
        shortDescription: draft.shortDescription,
        category: draft.category,
        order: draft.order,
        relatedNodeIds: draft.relatedNodeIds || [],
        relatedTags: draft.relatedTags || [],
        contentBlocks: sanitizeCodexBlocks(draft.contentBlocks || [])
      }
    })
  })

  const publishedDoc = await getDoc(documentRef)
  await addDoc(collection(db, CODEX_REVISIONS), {
    codexId: id,
    version: publishedDoc.data()?.version || 1,
    publishedSnapshot: publishedDoc.data()?.published || null,
    createdAt: serverTimestamp(),
    createdBy: actor?.uid || null
  })
  await addCodexAuditLog(id, 'publish', actor)
}

export async function unpublishCodexDoc(id, actor) {
  await updateDoc(doc(db, CODEX_DOCUMENTS, id), {
    status: 'draft',
    updatedAt: serverTimestamp(),
    updatedBy: actor?.uid || null
  })
  await addCodexAuditLog(id, 'unpublish', actor)
}

/** Vitrine beta : visible sans compte autorisé quand la beta Harmonist est active (admin uniquement). */
export async function setCodexPublicPreview(id, publicPreview, actor) {
  await updateDoc(doc(db, CODEX_DOCUMENTS, id), {
    publicPreview: !!publicPreview,
    updatedAt: serverTimestamp(),
    updatedBy: actor?.uid || null
  })
  await addCodexAuditLog(id, 'setPublicPreview', actor, { publicPreview: !!publicPreview })
}

export async function listPublishedCodexDocs(options = {}) {
  const publicTeaserOnly = !!options.publicTeaserOnly
  // Filtre status (+ publicPreview en beta vitrine). Tri par published.order côté client.
  let docsQ = query(collection(db, CODEX_DOCUMENTS), where('status', '==', 'published'))
  if (publicTeaserOnly) {
    docsQ = query(docsQ, where('publicPreview', '==', true))
  }
  const snap = await getDocs(docsQ)
  const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
  docs.sort((a, b) => {
    const ao = a.published?.order
    const bo = b.published?.order
    const na = Number.isFinite(Number(ao)) ? Number(ao) : 999
    const nb = Number.isFinite(Number(bo)) ? Number(bo) : 999
    if (na !== nb) return na - nb
    return String(a.id).localeCompare(String(b.id))
  })
  return docs
}

export async function listPublishedCodexEntries(options = {}) {
  const docs = await listPublishedCodexDocs(options)
  return docs.map(mapPublishedToEntry)
}

export async function listCodexRevisions(codexId) {
  const q = query(collection(db, CODEX_REVISIONS), where('codexId', '==', codexId), orderBy('version', 'desc'), limit(30))
  const snap = await getDocs(q)
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }))
}

export async function rollbackCodexDoc(codexId, revisionId, actor) {
  const revisionSnap = await getDoc(doc(db, CODEX_REVISIONS, revisionId))
  if (!revisionSnap.exists()) throw new Error('Révision introuvable')
  const revision = revisionSnap.data()
  await updateDoc(doc(db, CODEX_DOCUMENTS, codexId), {
    status: 'published',
    published: revision.publishedSnapshot || null,
    updatedAt: serverTimestamp(),
    updatedBy: actor?.uid || null,
    publishedAt: serverTimestamp(),
    publishedBy: actor?.uid || null
  })
  await addCodexAuditLog(codexId, 'rollback', actor, { revisionId })
}

export async function addCodexAuditLog(codexId, action, actor, extra = {}) {
  await addDoc(collection(db, CODEX_AUDIT_LOGS), {
    codexId,
    action,
    by: actor?.uid || null,
    at: serverTimestamp(),
    ...extra
  })
}

export async function deleteCodexDraft(id, actor) {
  await Promise.all([
    deleteDoc(doc(db, CODEX_DRAFTS, id)),
    updateDoc(doc(db, CODEX_DOCUMENTS, id), {
      status: 'archived',
      updatedAt: serverTimestamp(),
      updatedBy: actor?.uid || null
    })
  ])
  await addCodexAuditLog(id, 'archive', actor)
}

export async function hardDeleteCodexDoc(id, actor) {
  const revisionsQ = query(collection(db, CODEX_REVISIONS), where('codexId', '==', id), limit(200))
  const revisionsSnap = await getDocs(revisionsQ)
  const deleteRevisions = revisionsSnap.docs.map((revision) => deleteDoc(doc(db, CODEX_REVISIONS, revision.id)))

  await Promise.all([
    deleteDoc(doc(db, CODEX_DRAFTS, id)),
    deleteDoc(doc(db, CODEX_DOCUMENTS, id)),
    ...deleteRevisions
  ])
  await addCodexAuditLog(id, 'hardDelete', actor)
}

export async function installStarterCodexEntries(actor, options = {}) {
  const { publish = true, overwrite = false } = options
  const summary = {
    created: 0,
    updated: 0,
    skipped: 0,
    total: CODEX_ENTRIES.length,
    entries: []
  }

  for (const entry of CODEX_ENTRIES) {
    const id = entry.id
    const docRef = doc(db, CODEX_DOCUMENTS, id)
    const draftRef = doc(db, CODEX_DRAFTS, id)
    const docSnap = await getDoc(docRef)

    if (docSnap.exists() && !overwrite) {
      summary.skipped += 1
      summary.entries.push({
        id,
        relatedTags: entry.relatedTags || [],
        status: 'skipped'
      })
      continue
    }

    const draftPayload = {
      title: sanitizeText(entry.title || 'Sans titre'),
      shortDescription: sanitizeText(entry.shortDescription || ''),
      category: entry.category || 'parcours',
      order: Number.isFinite(entry.order) ? entry.order : 999,
      relatedNodeIds: Array.isArray(entry.relatedNodeIds) ? entry.relatedNodeIds : [],
      relatedTags: Array.isArray(entry.relatedTags) ? entry.relatedTags : [],
      contentBlocks: sanitizeCodexBlocks(entryToBlocks(entry)),
      etag: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    }

    const baseDoc = {
      slug: normalizeSlug(entry.id || entry.title),
      status: publish ? 'published' : 'draft',
      version: publish ? 1 : 0,
      createdAt: docSnap.exists() ? (docSnap.data().createdAt || serverTimestamp()) : serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: docSnap.exists() ? (docSnap.data().createdBy || actor?.uid || null) : (actor?.uid || null),
      updatedBy: actor?.uid || null,
      ...(publish
        ? {
            publishedAt: serverTimestamp(),
            publishedBy: actor?.uid || null,
            published: {
              title: draftPayload.title,
              shortDescription: draftPayload.shortDescription,
              category: draftPayload.category,
              order: draftPayload.order,
              relatedNodeIds: draftPayload.relatedNodeIds,
              relatedTags: draftPayload.relatedTags,
              contentBlocks: draftPayload.contentBlocks
            }
          }
        : { published: null })
    }

    await setDoc(docRef, baseDoc, { merge: true })
    await setDoc(draftRef, {
      ...draftPayload,
      updatedAt: serverTimestamp(),
      updatedBy: actor?.uid || null
    }, { merge: true })

    if (publish) {
      await addDoc(collection(db, CODEX_REVISIONS), {
        codexId: id,
        version: 1,
        publishedSnapshot: baseDoc.published,
        createdAt: serverTimestamp(),
        createdBy: actor?.uid || null
      })
    }

    await addCodexAuditLog(id, docSnap.exists() ? 'starterUpdate' : 'starterCreate', actor)

    if (docSnap.exists()) summary.updated += 1
    else summary.created += 1

    summary.entries.push({
      id,
      relatedTags: entry.relatedTags || [],
      status: docSnap.exists() ? 'updated' : 'created'
    })
  }

  return summary
}
